import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ClipboardPaste, Loader2, ArrowUpRight } from "lucide-react";
import { useWallet } from "@/context/WalletContext";
import { CHAINS, fmtNum, getChain } from "@/lib/chains";
import { fetchAssetBalance } from "@/lib/rpc";
import { sendAsset } from "@/lib/send";
import { assetsForChain } from "@/lib/tokens";
import { addTx } from "@/lib/txstore";
import ChainIcon from "@/components/ChainIcon";

const STABLE_COLOR = { USDT: "#26A17B", USDC: "#2775CA" };

export default function Send() {
  const nav = useNavigate();
  const { accounts } = useWallet();
  const [chainId, setChainId] = useState("eth");
  const [assetId, setAssetId] = useState("eth");
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const [balance, setBalance] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const chain = getChain(chainId);
  const assets = useMemo(() => assetsForChain(chainId), [chainId]);
  const asset = assets.find((a) => a.id === assetId) || assets[0];

  const pickChain = (id) => {
    setChainId(id);
    setAssetId(id); // default to native
  };

  const loadBal = useCallback(async () => {
    setBalance(await fetchAssetBalance(asset, accounts));
  }, [asset, accounts]);

  useEffect(() => { loadBal(); }, [loadBal]);

  const paste = async () => {
    try {
      const t = await navigator.clipboard.readText();
      setTo(t.trim());
      toast.success("Pasted from clipboard");
    } catch {
      toast.error("Clipboard unavailable — paste manually");
    }
  };

  const submit = async () => {
    setError("");
    if (!to) { setError("Enter a recipient address"); return toast.error("Enter a recipient address"); }
    if (!amount || Number(amount) <= 0) { setError("Enter a valid amount"); return toast.error("Enter a valid amount"); }
    if (Number(amount) > balance) { setError(`Insufficient balance — you have ${fmtNum(balance)} ${asset.symbol}`); return toast.error("Amount exceeds balance"); }
    setBusy(true);
    try {
      const hash = await sendAsset(asset, accounts, to, amount);
      addTx({ chainId, symbol: asset.symbol, to, amount, hash, status: "sent", type: "send" });
      toast.success(`${asset.symbol} sent`, { description: hash.slice(0, 24) + "…" });
      setTo(""); setAmount("");
      loadBal();
      nav("/activity");
    } catch (e) {
      const msg = (e.message || "Transaction failed").slice(0, 140);
      setError(msg);
      toast.error("Transaction failed", { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 pt-6">
      <button onClick={() => nav(-1)} className="mb-5 flex items-center gap-1 text-[#9CA3AF] hover:text-white">
        <ChevronLeft className="h-5 w-5" /> <span className="font-sans text-sm">Back</span>
      </button>
      <h1 className="font-outfit text-2xl font-extrabold">Send</h1>

      {/* chain selector */}
      <div className="mt-5 flex gap-2 overflow-x-auto pb-1">
        {CHAINS.map((c) => (
          <button
            key={c.id}
            data-testid={`send-chain-${c.id}`}
            onClick={() => pickChain(c.id)}
            className={`shrink-0 rounded-full border px-3 py-1.5 font-outfit text-xs font-semibold transition-colors ${
              chainId === c.id ? "border-neon bg-neon/10 text-neon" : "border-[#1F2430] text-[#9CA3AF]"
            }`}
          >
            {c.symbol}
          </button>
        ))}
      </div>

      {/* asset selector (native + tokens) */}
      <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
        {assets.map((a) => (
          <button
            key={a.id}
            data-testid={`send-asset-${a.id}`}
            onClick={() => setAssetId(a.id)}
            className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 font-outfit text-xs font-semibold transition-colors ${
              assetId === a.id ? "border-neon bg-neon/10 text-neon" : "border-[#1F2430] text-[#9CA3AF]"
            }`}
          >
            {a.kind === "token" && (
              <span className="h-2 w-2 rounded-full" style={{ background: STABLE_COLOR[a.symbol] || "#26A17B" }} />
            )}
            {a.kind === "native" ? `${a.symbol} (coin)` : a.symbol}
          </button>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-3 card-surface p-4">
        {asset.kind === "token" ? (
          <div className="relative">
            <div className="flex h-10 w-10 items-center justify-center rounded-full font-rajdhani text-[10px] font-bold text-white" style={{ background: STABLE_COLOR[asset.symbol] || "#26A17B" }}>
              {asset.symbol}
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-[#12141A]" style={{ background: chain.color }} />
          </div>
        ) : (
          <ChainIcon chain={chain} size={40} />
        )}
        <div className="flex-1">
          <p className="font-outfit text-sm font-semibold text-white">{asset.kind === "token" ? `${asset.symbol} on ${chain.name}` : chain.name}</p>
          <p className="font-sans text-xs text-[#6B7280]">Available</p>
        </div>
        <p className="font-rajdhani text-base font-semibold text-neon">{fmtNum(balance)} {asset.symbol}</p>
      </div>

      {/* recipient */}
      <label className="mb-1.5 mt-6 block font-sans text-xs text-[#9CA3AF]">Recipient address</label>
      <div className="flex gap-2">
        <input
          data-testid="recipient-address-input"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder={`${chain.symbol} address`}
          className="flex-1 rounded-xl border border-[#1F2430] bg-[#12141A] px-4 py-3 font-rajdhani text-sm text-white outline-none focus:border-neon/50"
        />
        <button data-testid="qr-scanner-trigger-button" onClick={paste} className="flex items-center justify-center rounded-xl border border-[#1F2430] px-3 text-[#9CA3AF] hover:text-neon">
          <ClipboardPaste className="h-5 w-5" />
        </button>
      </div>

      {/* amount */}
      <label className="mb-1.5 mt-5 block font-sans text-xs text-[#9CA3AF]">Amount</label>
      <div className="relative">
        <input
          data-testid="send-amount-input"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
          placeholder="0.00"
          inputMode="decimal"
          className="w-full rounded-xl border border-[#1F2430] bg-[#12141A] px-4 py-3 pr-28 font-rajdhani text-lg text-white outline-none focus:border-neon/50"
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-2">
          <button onClick={() => setAmount(String(balance))} className="rounded-lg bg-neon/10 px-2 py-1 font-rajdhani text-xs font-semibold text-neon">MAX</button>
          <span className="font-rajdhani text-sm text-[#9CA3AF]">{asset.symbol}</span>
        </div>
      </div>

      <p className="mt-3 font-sans text-xs text-[#6B7280]">
        {asset.kind === "token" ? `Network fee is paid in ${chain.symbol} (native).` : `Network fee is estimated automatically and paid in ${chain.symbol}.`}
      </p>

      {error && (
        <p data-testid="send-error-message" className="mt-3 rounded-xl border border-[#EF4444]/30 bg-[#EF4444]/10 px-4 py-3 font-sans text-sm text-[#EF4444]">
          {error}
        </p>
      )}

      <button
        data-testid="confirm-send-transaction-button"
        disabled={busy}
        onClick={submit}
        className="mt-7 flex w-full items-center justify-center gap-2 rounded-2xl bg-neon py-4 font-outfit font-bold text-[#08090C] transition-opacity disabled:opacity-50 active:scale-[0.98] neon-glow"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowUpRight className="h-5 w-5" />}
        {busy ? "Broadcasting…" : `Send ${asset.symbol}`}
      </button>
    </div>
  );
}
